import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Inline, Primary, Secondary, Sheet, Tag } from "@/components/backoffice/kit";
import { Toggle } from "@/components/account/Shell";

/** 8.12 Flags & mode — the platform Simple | Classic default, shops with an override, every feature flag behind a confirm sheet. */
export default function FlagsScreen() {
    const [d, setD] = useState(null);
    const [err, setErr] = useState("");
    const [ask, setAsk] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => {
        setErr("");
        api.get("/admin/flags").then(({ data }) => setD(data)).catch((e) => { setD({}); setErr(formatApiError(e.response?.data?.detail)); });
    }, []);
    useEffect(() => { load(); }, [load]);

    const confirm = async () => {
        setBusy(true);
        try {
            if (ask.kind === "mode") {
                await api.put("/admin/site-mode", { site_mode: ask.value });
                toast.success(`Platform default is now ${ask.value}`);
                window.location.reload();
                return;
            }
            await api.put("/admin/features", { features: { [ask.flag]: ask.value } });
            toast.success(`${ask.flag} is ${ask.value ? "on" : "off"}`);
            setAsk(null); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    if (d === null) return <div className="flex min-h-[30vh] items-center justify-center" data-testid="flags-loading"><Loader2 className="animate-spin" /></div>;
    return (
        <div className="grid gap-4" data-testid="flags-screen">
            <h1 className="text-[22px] font-semibold">Flags &amp; mode</h1>
            <Inline error={err} onRetry={load} testid="flags-error" />
            {d.features && (
                <>
                    <section className="grid gap-2 border border-[#1a1a1a] bg-white p-3" data-testid="flags-mode">
                        <h2 className="text-[16px] font-semibold">Platform default</h2>
                        <p className="text-[14px] text-[#4d4d4d]">Every customer screen, the signup and both back offices follow this unless a shop has its own override.</p>
                        <div className="flex gap-2">
                            {["simple", "classic"].map((m) => (
                                <button key={m} onClick={() => d.site_mode !== m && setAsk({ kind: "mode", value: m })} aria-pressed={d.site_mode === m} data-testid={`flags-mode-${m}`}
                                    className={`min-h-[44px] border px-4 text-[15px] font-semibold capitalize ${d.site_mode === m ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : "border-[#1a1a1a] bg-white"}`}>{m}</button>
                            ))}
                        </div>
                        <div data-testid="flags-overrides">
                            <p className="text-[14px] font-semibold">Shops with an override</p>
                            {(d.overrides || []).length === 0 ? <p className="text-[14px] text-[#4d4d4d]">None — every shop follows the platform.</p> : (
                                <ul className="flex flex-wrap gap-2 pt-1">
                                    {d.overrides.map((s) => <li key={s.slug}><Secondary to={`/admin?tab=shops&shop=${s.slug}`} testid={`flags-override-${s.slug}`}>{s.name} · {s.site_mode}</Secondary></li>)}
                                </ul>
                            )}
                        </div>
                    </section>
                    <section className="grid gap-2" data-testid="flags-features">
                        <h2 className="text-[16px] font-semibold">Feature flags</h2>
                        <p className="text-[14px] text-[#4d4d4d]">Off = vaulted: routes redirect home, menus hide, the API answers 404. Data stays. Each row is in <code>VAULT_INDEX.md</code>.</p>
                        <ul className="divide-y divide-[#767676] border-y border-[#767676]">
                            {(d.vault || []).map((f) => (
                                <li key={f.flag} className="grid items-center gap-x-3 gap-y-1 py-2 sm:grid-cols-[minmax(0,1fr)_auto]" data-testid={`flag-row-${f.flag}`}>
                                    <div>
                                        <p className="text-[15px] font-semibold"><code>{f.flag}</code> {f.on ? <Tag tone="green">on</Tag> : <Tag tone="grey">off</Tag>}</p>
                                        <p className="text-[14px] text-[#4d4d4d]">{f.what}</p>
                                    </div>
                                    <Toggle on={f.on} onChange={(v) => setAsk({ kind: "flag", flag: f.flag, value: v })} testid={`flag-toggle-${f.flag}`} labelOn="On" labelOff="Off" />
                                </li>
                            ))}
                        </ul>
                    </section>
                </>
            )}
            {ask && (
                <Sheet title={ask.kind === "mode" ? `Switch the platform to ${ask.value}?` : `Turn ${ask.flag} ${ask.value ? "on" : "off"}?`} onClose={() => setAsk(null)} testid="flags-confirm">
                    <p className="text-[15px]">
                        {ask.kind === "mode"
                            ? "Every shop without an override changes at once. The page reloads."
                            : ask.value ? "Its routes, menus and API come back for both modes." : "Its routes redirect home, its menus hide and its API answers 404. No data is deleted."}
                    </p>
                    <div className="mt-3"><Primary onClick={confirm} disabled={busy} testid="flags-confirm-go" full>Confirm</Primary></div>
                </Sheet>
            )}
        </div>
    );
}
